/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2024 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/

import React, { useEffect, useState } from "react";
import TopPanelCss from "components/topPanel/TopPanel.css";
import PropTypes from "prop-types";
import { discovery } from "dc-core";
import { CSSTransition } from "react-transition-group";
import ChevronDownIcon from "dc-icons/viewer2021/S_ChevronDownMedium_18_N.svg";
import ChevronUpIcon from "dc-icons/viewer2021/S_ChevronUpMedium_18_N.svg";
import { Flex } from "@react-spectrum/layout";
import { View } from "@react-spectrum/view";
import { ActionButton } from "@react-spectrum/button";
import { trackTopPanelClicked, trackTopPanelExpandedState, trackTopPanelVisible } from "analytics/events";

const TopPanelView = props => {
  const {
    topPanelVerbId,
    topPanelVisibility,
    bottomOffset,
    topPanelOptions,
    setFabVisibility,
    setTopPanelVisibility,
    isTopPanelExpaned,
    setTopPanelExpandedState,
    getPreviewDimensions
  } = props;
  const [TopPanelHeader, setTopPanelHeader] = useState(null);
  const [TopPanelContent, setTopPanelContent] = useState(null);
  const [topPanelDimensions, setTopPanelDimensions] = useState({});

  const showTopPanelCallback = status => {
    setTopPanelVisibility(status);
  };

  useEffect(() => {
    if (topPanelVerbId) {
      const opts = { context: "TopPanel", showCallback: showTopPanelCallback, setTopPanelExpandedState };
      discovery.callBootstrapMethod(topPanelVerbId, "initializeAction", opts);
      discovery
        .callBootstrapMethod(topPanelVerbId, "getSecondaryHeaderComponent", opts)
        .then(res => setTopPanelHeader(res));

      if (isTopPanelExpaned) {
        getExpandedComponent();
      }
    } else {
      setTopPanelHeader(null);
      setTopPanelContent(null);
    }
  }, [topPanelVerbId]);

  useEffect(() => {
    const changeOptionsForOrientation = () => {
      // Wait for UI to update as per the new orientation
      setTimeout(() => {
        const maxHeight = getMaxHeight();
        const minHeight = getMinHeight();
        setTopPanelDimensions({ maxHeight, minHeight });
      }, 100);
    };

    if (topPanelVerbId) {
      changeOptionsForOrientation();
      if (screen?.orientation) {
        screen.orientation.addEventListener("change", changeOptionsForOrientation);
      } else {
        window.addEventListener("orientationchange", changeOptionsForOrientation);
      }
    }

    // Cleanup function to remove the event listeners on unmount
    return () => {
      if (screen?.orientation) {
        screen.orientation.removeEventListener("change", changeOptionsForOrientation);
      } else {
        window.removeEventListener("orientationchange", changeOptionsForOrientation);
      }
    };
  }, [topPanelVerbId]);

  useEffect(() => {
    const mobileSize = window.matchMedia("(orientation: portrait)").matches ? 550 : 1000;
    if (window.matchMedia(`(max-width: ${mobileSize}px)`).matches) {
      setFabVisibility(!isTopPanelExpaned);
    }
    trackTopPanelExpandedState(topPanelVerbId, isTopPanelExpaned);
  }, [isTopPanelExpaned]);

  useEffect(() => {
    if (topPanelVerbId && topPanelVisibility) {
      trackTopPanelVisible(topPanelVerbId);
    }
  }, [topPanelVisibility]);

  const onPanelPress = () => {
    trackTopPanelClicked(topPanelVerbId);
    if (isTopPanelExpaned) {
      setTopPanelExpandedState(false);
      return;
    }
    getExpandedComponent();
  };

  const getExpandedComponent = async () => {
    if (!TopPanelContent) {
      const opts = { context: "TopPanel", clickContext: "TopPanel" };
      const expandedPanelComponent = await discovery.callBootstrapMethod(topPanelVerbId, "getExpandedComponent", opts);
      if (!expandedPanelComponent) return;
      setTopPanelContent(expandedPanelComponent);
    }
    setTopPanelExpandedState(true);
  };

  const getMaxHeight = () => {
    if (typeof topPanelOptions.maxHeight === "function") {
      const availableHeight = getPreviewDimensions().height - bottomOffset;
      return topPanelOptions.maxHeight(availableHeight);
    }
    return topPanelOptions.maxHeight;
  };

  const getMinHeight = () => {
    if (typeof topPanelOptions.minHeight === "function") {
      const availableHeight = getPreviewDimensions().height - bottomOffset;
      return topPanelOptions.minHeight(availableHeight);
    }
    return topPanelOptions.minHeight;
  };

  return TopPanelHeader ? (
    <Flex
      direction="column"
      data-testid="topPanelContainer"
      UNSAFE_className={TopPanelCss.topPanelContainer}
      UNSAFE_style={{
        maxHeight: topPanelDimensions.maxHeight,
        minHeight: topPanelDimensions.minHeight
      }}
      id="mobile-top-panel"
      isHidden={!topPanelVisibility}
    >
      <ActionButton
        isQuiet
        UNSAFE_className={TopPanelCss.topPanelHeader}
        data-testid="topPanelHeader"
        onPress={onPanelPress}
      >
        <Flex justifyContent="space-between" flex="1" alignItems="center">
          {TopPanelHeader}
          {isTopPanelExpaned && TopPanelContent ? <ChevronUpIcon /> : <ChevronDownIcon />}
        </Flex>
      </ActionButton>
      <CSSTransition
        in={isTopPanelExpaned}
        timeout={500}
        classNames={{
          enterActive: TopPanelCss.panelEnterActive,
          enterDone: TopPanelCss.panelEnterDone,
          exitActive: TopPanelCss.panelExitActive
        }}
        mountOnEnter
        unmountOnExit
      >
        <View UNSAFE_className={TopPanelCss.topPanelContent} data-testid="topPanelContent">
          {TopPanelContent}
        </View>
      </CSSTransition>
    </Flex>
  ) : null;
};

TopPanelView.propTypes = {
  topPanelVerbId: PropTypes.string,
  topPanelVisibility: PropTypes.bool,
  bottomOffset: PropTypes.number
};

TopPanelView.defaultProps = {
  topPanelVerbId: "",
  topPanelVisibility: false,
  bottomOffset: 0
};

export default TopPanelView;
